import type { AxiosError } from 'axios';
import cn from 'classnames';
import { type ReactNode, Suspense } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { AxiosErrorBoundary, AxiosErrorHandler, Loading } from 'ui';
import { GlobalSearchInput } from 'features/global-search';
import { logout } from '../../common/services/hooks/useLogout';
import { AppLogo } from './app-logo';
import { Navigation } from './navigation';
import { UserMenu } from './user-menu';

type P<T = unknown> = {
  aside?: ReactNode;
  children?: ReactNode;
  error?: AxiosError<T> | Error | null;
  fitCenterContent?: boolean;
  stretchCenter?: boolean;
};

const AppLayout = <T = unknown,>({ aside, children, error, fitCenterContent, stretchCenter }: P<T>) => {
  const location = useLocation();
  const navigate = useNavigate();

  const onLogOut = async () => {
    const { pathname } = location;
    // Hotfix so if user logs out from page that has public view and redirects to login page doesn't get Faild to load data message
    // It's important that navigate is called before logOut
    await navigate('/login', { state: { referrer: pathname } });
    await logout();
  };

  return (
    <section
      className={cn('app-layout', {
        'app-layout--stretch-center': stretchCenter,
        'app-layout--with-sidebar': Boolean(aside),
      })}
    >
      <AxiosErrorBoundary actions handleUnauthorized>
        <header className="app-layout__header">
          <div className="app-layout__header__content">
            <div className="app-layout__header__content__logo">
              <AppLogo />
            </div>
            <div className="app-layout__header__content__search-bar">
              <GlobalSearchInput onNavigate={navigate} />
            </div>
            <div className="app-layout__header__content__user-menu">
              <UserMenu onLogOut={onLogOut} />
            </div>
          </div>
        </header>

        <div className="app-layout__main">
          <nav className="app-layout__main__left-sidebar">
            <div className="app-layout__main__left-sidebar__content">
              <Navigation />
            </div>
          </nav>
          <AxiosErrorBoundary actions>
            <main className="app-layout__main__center">
              <Suspense fallback={<Loading />}>
                <div
                  className={cn('app-layout__main__center__content', {
                    'app-layout__main__center__content--fit-height': fitCenterContent,
                  })}
                >
                  <AxiosErrorHandler error={error}>{children}</AxiosErrorHandler>
                </div>
              </Suspense>
            </main>
            {aside ? (
              <aside className="app-layout__main__right-sidebar">
                <div className="app-layout__main__right-sidebar__content">{aside}</div>
              </aside>
            ) : null}
          </AxiosErrorBoundary>
        </div>
      </AxiosErrorBoundary>
    </section>
  );
};

export default AppLayout;
